import type { ReactElement } from "react";
import { useMemo, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Button, Combobox, DropdownButton, Modal } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { TopicOptionLabel } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/prompts-table/PromptsTable.styled";
import type { IModifiablePrompt } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/types";
import { allTrackers } from "app/services/track/track";

interface IBulkChangeTopicModalHook {
    openModal: (prompts: IModifiablePrompt[]) => void;
    modal: ReactElement;
}

export const useBulkChangeTopicModal = (
    topics: string[],
    onTopicChange: (prompts: IModifiablePrompt[], newTopic: string) => void,
): IBulkChangeTopicModalHook => {
    const [isOpen, setOpen] = useState<boolean>(false);
    const [isTopicsDropdownOpen, setTopicsDropdownOpen] = useState<boolean>(false);
    const [promptsToEdit, setPromptsToEdit] = useState<IModifiablePrompt[]>([]);
    const [selectedTopic, setSelectedTopic] = useState<string>("");
    const initialTopicLabel = useMemo<string>(() => {
        if (!promptsToEdit.length) {
            return "";
        }

        const uniqueTopics = new Set(promptsToEdit.map((prompt) => prompt.currentTopicName));
        const multipleTopics = uniqueTopics.size > 1;
        return multipleTopics
            ? translate("llm.prompts.edit.prompts-table.bulk-change-topic.multiple-topics-placeholder")
            : [...uniqueTopics][0];
    }, [promptsToEdit]);

    const isSubmitAvailable = useMemo<boolean>(() => {
        if (!isOpen || !selectedTopic) {
            return false;
        }

        return promptsToEdit.some((prompt) => prompt.currentTopicName !== selectedTopic);
    }, [isOpen, selectedTopic, promptsToEdit]);

    const openModal = (prompts: IModifiablePrompt[]): void => {
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "bulk topic change/open", { count: prompts.length });
        setPromptsToEdit(prompts);

        const uniqueTopics = new Set(prompts.map((prompt) => prompt.currentTopicName));
        setSelectedTopic(uniqueTopics.size === 1 ? [...uniqueTopics][0] : "");
        setOpen(true);
    };

    const onTopicClick = (topic: string): void => {
        setTopicsDropdownOpen(false);
        setSelectedTopic(topic);
    };

    const closeModal = (): void => {
        setOpen(false);
        setTopicsDropdownOpen(false);
        setPromptsToEdit([]);
        setSelectedTopic("");
    };

    const submit = (): void => {
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "bulk topic change/submit");
        onTopicChange(promptsToEdit, selectedTopic);
        closeModal();
    };

    return {
        openModal,
        modal: (
            <Modal
                isOpen={isOpen}
                onRequestClose={closeModal}
                shouldCloseOnEsc
                shouldCloseOnOverlayClick
                customStyles={{
                    content: {
                        width: "400px",
                    },
                }}
            >
                <Modal.Header>
                    <Modal.Title tooltip={translate("llm.prompts.edit.prompts-table.bulk-change-topic.tooltip")}>
                        {translate("llm.prompts.edit.prompts-table.bulk-change-topic.title")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Content>
                    <Flex
                        flexDirection="column"
                        padding="16px 0 24px 0"
                        gap="16px"
                    >
                        <Flex
                            flexDirection="column"
                            gap="4px"
                        >
                            <Text
                                fontVariant="caption"
                                color={colorsPalettes.carbon[300]}
                            >
                                {translate("llm.prompts.edit.prompts-table.modal.field.topic.title")}
                            </Text>
                            <Combobox
                                isOpen={isTopicsDropdownOpen}
                                onOpenChange={setTopicsDropdownOpen}
                                placement="bottom"
                                matchTriggerWidth
                                data-automation="prompts-report-bulk-topic-change-dropdown"
                            >
                                <Combobox.Trigger>
                                    <Flex width="100%">
                                        <DropdownButton isOpen={isTopicsDropdownOpen}>
                                            <Text
                                                fontVariant="input2"
                                                color={
                                                    selectedTopic
                                                        ? colorsPalettes.carbon[500]
                                                        : colorsPalettes.carbon[300]
                                                }
                                            >
                                                {selectedTopic || initialTopicLabel}
                                            </Text>
                                        </DropdownButton>
                                    </Flex>
                                </Combobox.Trigger>
                                <Combobox.Content>
                                    {topics.map((topic) => (
                                        <Combobox.Option
                                            key={topic}
                                            id={topic}
                                            textValue={topic}
                                            onClick={() => onTopicClick(topic)}
                                        >
                                            <TopicOptionLabel>{topic}</TopicOptionLabel>
                                        </Combobox.Option>
                                    ))}
                                </Combobox.Content>
                            </Combobox>
                        </Flex>
                    </Flex>
                </Modal.Content>
                <Modal.Footer>
                    <Button
                        type="flat"
                        onClick={closeModal}
                        data-automation="prompts-report-bulk-topic-change-cancel"
                    >
                        {translate("global.cancel")}
                    </Button>
                    <Button
                        type="primary"
                        onClick={submit}
                        isDisabled={!isSubmitAvailable}
                        data-automation="prompts-report-bulk-topic-change-submit"
                    >
                        {translate("common.submit")}
                    </Button>
                </Modal.Footer>
            </Modal>
        ),
    };
};
